import {
  seenVersionSchema
} from "../chunk-L6ZPEPR3.js";
import {
  DEFAULT_PLATFORM_SETTINGS,
  LISTING_RIGHTS_LIMIT,
  PLATFORM_ASSET_KEYS,
  PLATFORM_ASSET_RULES,
  PLATFORM_SETTINGS_KEY,
  POLICY_COMPANY_PLACEHOLDERS,
  SOCIAL_LINK_KEYS,
  SOCIAL_LINK_LABELS,
  SOCIAL_LINK_LABELS_I18N,
  appSemverSchema,
  appVersionResponseSchema,
  catalogOptionSchema,
  channelMinFollowersSchema,
  compareAppVersions,
  contractTermsSchema,
  fillCompanyPlaceholders,
  formatFollowerBoundary,
  formatFollowerCount,
  licenseAmendmentSchema,
  licenseSuspensionSchema,
  listingOfferResponseSchema,
  lowestMinFollowers,
  manualSocialPlatformSchema,
  orderGroupMemberSchema,
  payoutAccountSchema,
  platformAssetKeySchema,
  platformAssetMetaSchema,
  platformAssetPath,
  platformBankAccountSchema,
  platformCatalogSchema,
  platformCommunitySettingsSchema,
  platformCompanySettingsSchema,
  platformListingSettingsSchema,
  platformPaymentSettingsSchema,
  platformSettingsSchema,
  platformSiteSettingsSchema,
  platformSiteTextSchema,
  platformSocialLinksSchema,
  platformSponsorshipSettingsSchema,
  resolveCommunityTier,
  royaltyReportResponseSchema,
  siteConfigResponseSchema,
  socialChannelRequestStatusSchema,
  socialPlatformSchema,
  sponsorshipCategorySchema,
  sponsorshipRequirementsSchema,
  sponsorshipResponseSchema,
  supportInquiryReplyRequestSchema,
  updateAppVersionRequestSchema,
  updateCommunitySettingsRequestSchema,
  updateCompanySettingsRequestSchema,
  updateListingSettingsRequestSchema,
  updatePaymentSettingsRequestSchema,
  updatePlatformSocialLinksRequestSchema,
  updateSiteSettingsRequestSchema,
  updateSponsorshipSettingsRequestSchema
} from "../chunk-RTE45FZ6.js";
import {
  dateRangeQuerySchema,
  noteItemsTextSchema,
  orderTransitionActorSchema,
  pageQuerySchema,
  pageResponseSchema
} from "../chunk-LBZDZW6L.js";

// src/admin/auth.schema.ts
import { z } from "zod";
var adminSignInRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1),
  /** ADMIN_MFA_ENABLED=true 일 때 필수 (등록 후) */
  mfaCode: z.string().regex(/^\d{6}$/, "MFA \uCF54\uB4DC\uB294 6\uC790\uB9AC \uC22B\uC790").optional()
});
var adminTokensResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number()
});
var adminMfaEnrollmentResponseSchema = z.object({
  mfaEnrollmentRequired: z.literal(true),
  /** base32 secret — Authenticator 앱에 수동 입력용 */
  secret: z.string(),
  /** otpauth:// URI — QR 코드 생성용 */
  otpauthUrl: z.string()
});
var adminSignInResponseSchema = z.union([
  adminTokensResponseSchema,
  adminMfaEnrollmentResponseSchema
]);
var adminRefreshRequestSchema = z.object({
  /** 웹 어드민은 httpOnly 쿠키 사용 */
  refreshToken: z.string().min(1).optional()
});

// src/admin/orders.schema.ts
import { DepositReturnKind, DepositReturnStatus, DisputeResolution, FulfillmentType, IpRightType, LicenseStatus, OrderStatus, PaymentKind, PaymentMethod, PaymentStatus, PayoutClawbackStatus, RefundStatus, SettlementStatus, ContractTerminationStatus, TerminationKind, TerminationRefundMode, ViolationReportResolution } from "@inflip/shared";
import { z as z2 } from "zod";
var dateOnly = z2.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");
var adminListOrdersQuerySchema = pageQuerySchema.extend({
  status: z2.nativeEnum(OrderStatus).optional(),
  buyerId: z2.string().uuid().optional(),
  /** 주문 ID prefix / 입금 참조 / 구매자·판매자 이메일·이름 / 상품 제목 검색 */
  q: z2.string().max(200).optional(),
  /** 추가금 입금 대기가 있는 주문만 (주문 상태와 별개) */
  pendingAddon: z2.enum([
    "true",
    "false"
  ]).transform((v) => v === "true").optional(),
  /** 처리 중인 판매자 위반 신고가 있는 주문만 (주문 상태와 별개) */
  openViolationReport: z2.enum([
    "true",
    "false"
  ]).transform((v) => v === "true").optional(),
  /** 발급 지연 주문만 — 입금 확인 뒤 결제 설정의 기준 일수가 지나도록 발급 전(PAID) */
  issueDelayed: z2.enum([
    "true",
    "false"
  ]).transform((v) => v === "true").optional(),
  /** 생성일 범위 (포함) */
  dateFrom: dateOnly.optional(),
  dateTo: dateOnly.optional()
});
var confirmPaymentRequestSchema = z2.object({
  /** 은행 내역의 실제 입금자명 — 안내한 입금자명과 다르면 확인 거부 (아래 사유가 있으면 처리) */
  depositReference: z2.string().trim().min(1, "\uC740\uD589 \uB0B4\uC5ED\uC758 \uC785\uAE08\uC790\uBA85\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(64),
  /** 은행 내역의 실제 입금 금액 — 결제 금액과 다르면 확인 거부 */
  receivedAmount: z2.number().int().positive(),
  /** 입금자명이 다른데 구매자 본인 입금을 입증받은 경우의 근거 (예: 이체확인증 확인) */
  depositorOverrideReason: z2.string().trim().min(5, "\uD655\uC778\uD55C \uADFC\uAC70\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500).optional(),
  /** 어드민 메모 — audit log 에 첨부 */
  note: z2.string().max(500).optional(),
  /** 초과 입금 — 결제 금액만큼 확인하고 넘친 금액은 입금 반환으로 (사유·반환 계좌 필수) */
  overpayment: z2.lazy(() => overpaymentReturnSchema).optional()
});
var cancelOrderRequestSchema = z2.object({
  reason: z2.string().min(1).max(500)
});
var dismissDisputeRequestSchema = z2.object({
  note: z2.string().trim().min(5).max(1e3)
});
var resolveViolationReportRequestSchema = z2.object({
  resolution: z2.enum([
    ViolationReportResolution.ACTIONED,
    ViolationReportResolution.DISMISSED
  ]),
  note: z2.string().trim().min(5).max(1e3)
});
var adminLicenseDraftSchema = z2.object({
  startsAt: dateOnly.nullable(),
  specialTerms: z2.string().nullable(),
  updatedAt: z2.string().datetime(),
  /** 구매자 확인 시각 — 판매자는 확인된 내용 그대로만 발급한다 */
  buyerConfirmedAt: z2.string().datetime().nullable()
});
var adminOrderLicenseSchema = z2.object({
  id: z2.string().uuid(),
  licenseKey: z2.string(),
  status: z2.nativeEnum(LicenseStatus),
  issuedAt: z2.string().datetime(),
  startsAt: z2.string().datetime(),
  expiresAt: z2.string().datetime().nullable(),
  specialTerms: z2.string().nullable(),
  /** 수정 제안 때 화면이 본 버전으로 보낸다 */
  updatedAt: z2.string().datetime()
});
var proposeLicenseAmendmentRequestSchema = z2.object({
  startsOn: dateOnly,
  expiryMode: z2.enum([
    "FOLLOW",
    "MANUAL"
  ]),
  /** MANUAL 일 때 마지막 사용일 (계약 시간대) */
  lastUseOn: dateOnly.optional(),
  specialTerms: noteItemsTextSchema(2e3).optional(),
  reason: z2.string().trim().min(5, "\uC218\uC815 \uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(1e3),
  /** 화면이 본 라이선스 버전 (license.updatedAt) */
  seenLicenseUpdatedAt: z2.string().datetime()
}).refine((v) => v.expiryMode !== "MANUAL" || v.lastUseOn !== void 0, {
  path: [
    "lastUseOn"
  ],
  message: "\uB9C8\uC9C0\uB9C9 \uC0AC\uC6A9\uC77C\uC744 \uACE8\uB77C \uC8FC\uC138\uC694"
});
var suspendLicenseRequestSchema = z2.object({
  reason: z2.string().trim().min(5, "\uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(1e3),
  seenLicenseUpdatedAt: z2.string().datetime()
});
var resumeLicenseRequestSchema = z2.object({
  note: z2.string().trim().max(1e3).optional()
});
var refundBankAccountSchema = z2.object({
  bank: z2.string().min(1).max(50),
  accountNumber: z2.string().min(4).max(50),
  holder: z2.string().min(1).max(100)
});
var createRefundRequestSchema = z2.object({
  amount: z2.number().int().positive(),
  reason: z2.string().min(1).max(500),
  /** 무통장 결제 환불이면 필수 */
  bankAccount: refundBankAccountSchema.optional()
});
var rejectRefundRequestSchema = z2.object({
  reason: z2.string().min(1).max(500)
});
var adminPayoutClawbackSchema = z2.object({
  id: z2.string().uuid(),
  status: z2.nativeEnum(PayoutClawbackStatus),
  amount: z2.number().int(),
  sellerId: z2.string().uuid(),
  dueAt: z2.string().datetime(),
  overdueAt: z2.string().datetime().nullable(),
  /** 기한 경과로 판매자 접속을 정지했는지 */
  suspendedSeller: z2.boolean(),
  collectedAt: z2.string().datetime().nullable(),
  collectionNote: z2.string().nullable(),
  cancelledAt: z2.string().datetime().nullable(),
  createdAt: z2.string().datetime()
});
var collectPayoutClawbackRequestSchema = z2.object({
  /** 입금일·입금자명 등 확인 근거 */
  note: z2.string().trim().min(2, "\uC785\uAE08 \uD655\uC778 \uB0B4\uC6A9\uC744 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});
var adminRefundResponseSchema = z2.object({
  id: z2.string().uuid(),
  orderId: z2.string().uuid(),
  paymentId: z2.string().uuid(),
  status: z2.nativeEnum(RefundStatus),
  amount: z2.number().int(),
  currency: z2.string(),
  method: z2.nativeEnum(PaymentMethod),
  reason: z2.string(),
  bankAccount: refundBankAccountSchema.nullable(),
  requestedBy: z2.string().uuid(),
  decidedAt: z2.string().datetime().nullable(),
  rejectReason: z2.string().nullable(),
  completedAt: z2.string().datetime().nullable(),
  createdAt: z2.string().datetime(),
  /** 정산 지급 뒤 환불이면 판매자 회수 */
  clawback: adminPayoutClawbackSchema.nullable().optional()
});
var exceptionReason = z2.string().trim().min(5, "\uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500);
var overpaymentReturnSchema = z2.object({
  reason: exceptionReason,
  bankAccount: refundBankAccountSchema
});
var returnDepositRequestSchema = z2.object({
  kind: z2.enum([
    DepositReturnKind.UNDERPAYMENT,
    DepositReturnKind.LATE_DEPOSIT
  ]),
  /** 은행 내역의 실제 입금액 */
  receivedAmount: z2.number().int().positive(),
  /** 은행 내역의 입금자명 */
  depositorName: z2.string().trim().min(1, "\uC740\uD589 \uB0B4\uC5ED\uC758 \uC785\uAE08\uC790\uBA85\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(64),
  reason: exceptionReason,
  bankAccount: refundBankAccountSchema
});
var reviveOrderRequestSchema = z2.object({
  receivedAmount: z2.number().int().positive(),
  depositReference: z2.string().trim().min(1, "\uC740\uD589 \uB0B4\uC5ED\uC758 \uC785\uAE08\uC790\uBA85\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(64),
  depositorOverrideReason: z2.string().trim().min(5).max(500).optional(),
  reason: exceptionReason,
  overpayment: overpaymentReturnSchema.optional()
});
var adminReviveCheckSchema = z2.object({
  /** 되살릴 수 없는 이유 — 비어 있으면 되살릴 수 있다 */
  blockers: z2.array(z2.string()),
  /** 함께 되살아나는 주문 (묶음이면 묶음 전체) */
  orderIds: z2.array(z2.string().uuid()),
  payment: z2.object({
    id: z2.string().uuid(),
    amount: z2.number().int(),
    depositReference: z2.string().nullable(),
    status: z2.nativeEnum(PaymentStatus)
  }).nullable()
});
var completeDepositReturnRequestSchema = z2.object({
  /** 송금 내역 등 — 기록에 남는다 */
  note: exceptionReason
});
var updateDepositReturnAccountRequestSchema = z2.object({
  bankAccount: refundBankAccountSchema,
  reason: exceptionReason
});
var adminDepositReturnSchema = z2.object({
  id: z2.string().uuid(),
  orderId: z2.string().uuid(),
  kind: z2.nativeEnum(DepositReturnKind),
  status: z2.nativeEnum(DepositReturnStatus),
  receivedAmount: z2.number().int(),
  amount: z2.number().int(),
  depositorName: z2.string(),
  reason: z2.string(),
  bankAccount: refundBankAccountSchema,
  accountUpdateReason: z2.string().nullable(),
  requestedBy: z2.string().uuid(),
  completedAt: z2.string().datetime().nullable(),
  completionNote: z2.string().nullable(),
  createdAt: z2.string().datetime()
});
var adminListDepositReturnsQuerySchema = pageQuerySchema.merge(dateRangeQuerySchema).extend({
  status: z2.nativeEnum(DepositReturnStatus).optional()
});
var adminDepositReturnListItemSchema = adminDepositReturnSchema.extend({
  orderGroupId: z2.string().uuid().nullable(),
  orderStatus: z2.nativeEnum(OrderStatus),
  listingTitle: z2.string().nullable(),
  buyerId: z2.string().uuid(),
  buyerName: z2.string().nullable(),
  buyerEmail: z2.string().nullable()
});
var adminDepositReturnListResponseSchema = pageResponseSchema(adminDepositReturnListItemSchema);
var cancelTerminationRequestSchema = z2.object({
  reason: z2.string().trim().min(5, "\uCDE8\uC18C \uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});
var terminateOrderRequestSchema = z2.object({
  kind: z2.nativeEnum(TerminationKind),
  reason: z2.string().trim().min(5, "\uD574\uC9C0 \uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(1e3),
  refundMode: z2.nativeEnum(TerminationRefundMode),
  /** 부분 환불(선불) 금액 — PARTIAL 일 때만 */
  refundAmount: z2.number().int().positive().optional(),
  /** 무통장 결제의 부분·전액 환불 수취 계좌 */
  bankAccount: refundBankAccountSchema.optional()
}).refine((v) => v.refundMode !== TerminationRefundMode.PARTIAL || v.refundAmount !== void 0, {
  path: [
    "refundAmount"
  ],
  message: "\uBD80\uBD84 \uD658\uBD88 \uAE08\uC561\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694"
});
var adminTerminationPreviewSchema = z2.object({
  /** 해지할 수 없으면 이유 */
  blockedReason: z2.string().nullable(),
  status: z2.nativeEnum(OrderStatus),
  totalAmount: z2.number().int(),
  refundedAmount: z2.number().int(),
  /** 지금 돌려줄 수 있는 최대 선불 = 주문 금액 − 완료 환불 */
  refundableAmount: z2.number().int(),
  platformFeeRateBp: z2.number().int(),
  needsBankAccount: z2.boolean(),
  settlement: z2.object({
    status: z2.nativeEnum(SettlementStatus),
    grossAmount: z2.number().int(),
    netAmount: z2.number().int(),
    clawedBackAmount: z2.number().int()
  }).nullable(),
  /** 구매확정 전 해지라 정산을 새로 만드는지 (전액 환불(선불)이면 만들지 않음) */
  createsSettlement: z2.boolean(),
  hasLicense: z2.boolean(),
  /** 로열티 계약 — 지금 해지하면 보고할 달 (계약 시간대). 로열티 계약이 아니면 null */
  royalty: z2.object({
    /** 마지막 사용일 'YYYY-MM-DD' */
    lastDay: z2.string(),
    /** 해지일이 있는 달 'YYYY-MM' */
    lastPeriod: z2.string(),
    /** 아직 보고되지 않은 달 (해지일 달까지) */
    unreportedPeriods: z2.array(z2.string())
  }).nullable(),
  /** 완전 독점 계약 — 해지하면 다른 구매자가 살 수 있다 */
  fullyExclusive: z2.boolean()
});
var adminOrderTerminationSchema = z2.object({
  id: z2.string().uuid(),
  requesterRole: z2.enum([
    "BUYER",
    "SELLER",
    "ADMIN"
  ]),
  kind: z2.nativeEnum(TerminationKind),
  reason: z2.string(),
  refundMode: z2.nativeEnum(TerminationRefundMode),
  refundAmount: z2.number().int(),
  refundId: z2.string().uuid().nullable(),
  /** 해지와 함께 만든 환불의 상태 — 비환불이면 null */
  refundStatus: z2.nativeEnum(RefundStatus).nullable(),
  executedBy: z2.string().uuid().nullable(),
  executedAt: z2.string().datetime().nullable(),
  /** EXECUTED 면 지금 해지 중, CANCELLED 면 취소된 기록 (정책: 해지 기록은 지우지 않는다) */
  status: z2.nativeEnum(ContractTerminationStatus).optional(),
  cancelledBy: z2.string().uuid().nullable().optional(),
  cancelledAt: z2.string().datetime().nullable().optional(),
  cancelReason: z2.string().nullable().optional()
});
var adminPaymentResponseSchema = z2.object({
  id: z2.string().uuid(),
  method: z2.nativeEnum(PaymentMethod),
  /** INITIAL = 계약 금액, ADDON = 발급 전 협의로 늘어난 추가금 */
  kind: z2.nativeEnum(PaymentKind),
  status: z2.nativeEnum(PaymentStatus),
  amount: z2.number().int(),
  currency: z2.string(),
  /** 추가금 사유 (ADDON 전용) */
  memo: z2.string().nullable(),
  depositReference: z2.string().nullable(),
  /** 입금 확인 때 어드민이 은행 내역에서 옮겨 적은 실제 입금자명 */
  receivedDepositorName: z2.string().nullable(),
  /** 안내한 이름과 달랐는데 확인한 근거 */
  depositorOverrideReason: z2.string().nullable(),
  depositDueAt: z2.string().datetime().nullable(),
  confirmedAt: z2.string().datetime().nullable(),
  confirmedBy: z2.string().uuid().nullable()
});
var adminProposalHistorySchema = z2.object({
  proposalId: z2.string().uuid(),
  rounds: z2.array(z2.object({
    round: z2.number().int().positive(),
    proposedBy: z2.string().uuid().nullable(),
    proposedAt: z2.string().datetime().nullable(),
    message: z2.string().nullable(),
    terms: contractTermsSchema.nullable()
  })),
  acceptedBy: z2.string().uuid().nullable(),
  acceptedAt: z2.string().datetime().nullable()
});
var adminPastDisputeSchema = z2.object({
  id: z2.string().uuid(),
  disputedAt: z2.string().datetime(),
  reason: z2.string(),
  /** 증거 이미지 수 — 보기 주소는 GET /orders/:id/dispute-evidence?recordId= */
  evidenceCount: z2.number().int().min(0),
  resolution: z2.nativeEnum(DisputeResolution).nullable(),
  resolutionNote: z2.string().nullable(),
  resolvedAt: z2.string().datetime().nullable()
});
var adminViolationReportSchema = z2.object({
  id: z2.string().uuid(),
  reporterId: z2.string().uuid(),
  reason: z2.string(),
  /** 증거 이미지 수 — 보기 주소는 GET /orders/:id/violation-reports/:reportId/evidence */
  evidenceCount: z2.number().int().min(0),
  resolution: z2.nativeEnum(ViolationReportResolution).nullable(),
  resolutionNote: z2.string().nullable(),
  resolvedAt: z2.string().datetime().nullable(),
  createdAt: z2.string().datetime()
});
var adminOrderResponseSchema = z2.object({
  proposalHistory: adminProposalHistorySchema.nullish(),
  terms: contractTermsSchema.nullish(),
  id: z2.string().uuid(),
  buyerId: z2.string().uuid(),
  buyerName: z2.string().nullable(),
  buyerEmail: z2.string().nullable(),
  listingId: z2.string().uuid(),
  listingTitle: z2.string().nullable(),
  rightType: z2.nativeEnum(IpRightType),
  sellerId: z2.string().uuid().nullable(),
  sellerName: z2.string().nullable(),
  status: z2.nativeEnum(OrderStatus),
  /** 계약 시간대 (IANA) — 주문 시점 판매자 시간대로 고정. 계약 날짜는 이 시간대로 계산·표시 */
  timeZone: z2.string(),
  fulfillmentType: z2.nativeEnum(FulfillmentType),
  /** 계약 금액 — 주문(협의 수락) 때 정한 선불 금액 */
  contractAmount: z2.number().int(),
  /** 확인된 추가금 합 */
  addonAmount: z2.number().int(),
  /** 결제 총액 = 계약 금액 + 확인된 추가금 */
  totalAmount: z2.number().int(),
  currency: z2.string(),
  paidAt: z2.string().datetime().nullable(),
  /** 발급 지연이면 입금 확인 뒤 지난 날수 (결제 설정의 발급 지연 기준), 아니면 null */
  licenseIssueDelayedDays: z2.number().int().nullable(),
  /** 이 주문에 고정된 플랫폼 수수료(bp) — 주문 시점의 결제 설정 */
  platformFeeRateBp: z2.number().int(),
  /** 구매확정 — 기한·시각·주체(USER 구매자 확정 / SYSTEM 기한 경과 자동 확정) */
  purchaseConfirmDueAt: z2.string().datetime().nullable(),
  purchaseConfirmedAt: z2.string().datetime().nullable(),
  purchaseConfirmedBy: orderTransitionActorSchema.nullable(),
  /** 분쟁 — 사유·접수 시각, 처리 결과(기각 = 정산 진행 / 전액 환불)·처리 사유·시각 */
  disputeReason: z2.string().nullable(),
  /** 구매자가 분쟁 때 첨부한 증거 이미지 수 — 보기 주소는 GET /orders/:id/dispute-evidence */
  disputeEvidenceCount: z2.number().int().min(0),
  disputedAt: z2.string().datetime().nullable(),
  disputeResolution: z2.nativeEnum(DisputeResolution).nullable(),
  disputeResolutionNote: z2.string().nullable(),
  disputeResolvedAt: z2.string().datetime().nullable(),
  /** 지난 분쟁 (최근 것부터, 상세에서만) — 지금(마지막) 분쟁은 위 dispute* 필드 */
  disputeHistory: z2.array(adminPastDisputeSchema).optional(),
  /** 판매자 위반 신고 (최근 것부터, 상세에서만) */
  violationReports: z2.array(adminViolationReportSchema).optional(),
  /** 정산이 이미 지급됐는지 (상세에서만) — 지급 뒤 분쟁은 기각하면 지급 완료로 돌아간다 */
  settlementPaidOut: z2.boolean().optional(),
  payment: adminPaymentResponseSchema.nullable(),
  /** 추가금 결제 내역 (요청 시각순) — PENDING 이 있으면 입금 확인 대상 */
  addonPayments: z2.array(adminPaymentResponseSchema),
  refunds: z2.array(adminRefundResponseSchema),
  /** 발급 전 판매자가 채팅에서 작성한 발급 예정 내용 — 발급되면 license 로 옮겨지고 null */
  licenseDraft: adminLicenseDraftSchema.nullable(),
  /** 발급된 라이선스 (특약 포함) */
  license: adminOrderLicenseSchema.nullable(),
  /** 라이선스 수정 제안 — 진행 중 + 이력, 최신순 (상세 조회에서만) */
  licenseAmendments: z2.array(licenseAmendmentSchema).optional(),
  /** 라이선스 사용 중지 이력 (상세에서만) */
  licenseSuspensions: z2.array(licenseSuspensionSchema).optional(),
  /** 만료 후 입금으로 되살린 기록 */
  revivedAt: z2.string().datetime().nullable().optional(),
  reviveReason: z2.string().nullable().optional(),
  /** 입금 반환 내역 (초과·부족·만료 후 입금) */
  depositReturns: z2.array(adminDepositReturnSchema).optional(),
  /** 계약 해지 — 해지 시각과 기록 (사유·선불 처리) */
  terminatedAt: z2.string().datetime().nullable().optional(),
  termination: adminOrderTerminationSchema.nullable().optional(),
  cancelledAt: z2.string().datetime().nullable(),
  /** 취소 주체 — SYSTEM 이면 입금 기한 만료(미입금 취소), USER/ADMIN 이면 주문 취소 */
  cancelledBy: orderTransitionActorSchema.nullable().optional(),
  /** 구매자가 등록한 환불 계좌 — 환불·입금 반환 계좌 칸을 미리 채운다 (상세 조회에서만) */
  buyerRefundAccount: payoutAccountSchema.nullable().optional(),
  /** 묶음 번호 — 한 번에 함께 주문한 주문들이 공유하고, 입금 안내 1건이 여기에 붙는다 */
  orderGroupId: z2.string().uuid().nullable().optional(),
  /** 묶음 안 주문 수 — 2 이상이면 묶음 주문 (목록·상세) */
  groupSize: z2.number().int().optional(),
  /** 같은 결제 묶음의 주문 전체(이 주문 포함, 주문순) — 상세 조회에서만. 1건이면 묶음 아님 */
  groupOrders: z2.array(orderGroupMemberSchema).optional(),
  createdAt: z2.string().datetime(),
  updatedAt: z2.string().datetime()
});
var adminOrderListResponseSchema = pageResponseSchema(adminOrderResponseSchema);
var adminListRefundsQuerySchema = pageQuerySchema.merge(dateRangeQuerySchema).extend({
  status: z2.nativeEnum(RefundStatus).optional(),
  /** 이 상태의 정산 회수가 걸린 환불만 (예: 기한 지남) */
  clawbackStatus: z2.nativeEnum(PayoutClawbackStatus).optional()
});
var adminRefundListItemSchema = adminRefundResponseSchema.extend({
  orderStatus: z2.nativeEnum(OrderStatus),
  buyerName: z2.string().nullable(),
  buyerEmail: z2.string().nullable()
});
var adminRefundListResponseSchema = pageResponseSchema(adminRefundListItemSchema);
var adminDisputeEvidenceResponseSchema = z2.object({
  items: z2.array(z2.object({
    filename: z2.string(),
    mime: z2.string(),
    sizeBytes: z2.number().int(),
    url: z2.string().url()
  }))
});
var adminDisputeEvidenceQuerySchema = z2.object({
  recordId: z2.string().uuid().optional()
});
var adminOrderTodoCountsSchema = z2.object({
  PAYMENT_PENDING: z2.number().int(),
  SETTLEMENT_PENDING: z2.number().int(),
  DISPUTED: z2.number().int(),
  /** 입금 대기 중인 추가금이 있는 주문 수 */
  ADDON_PENDING: z2.number().int(),
  /** 처리 중인 판매자 위반 신고가 있는 주문 수 */
  VIOLATION_REPORTED: z2.number().int()
});

// src/admin/payments.schema.ts
import { OrderStatus as OrderStatus2, PaymentKind as PaymentKind2, PaymentStatus as PaymentStatus2 } from "@inflip/shared";
import { z as z3 } from "zod";
var adminListPaymentsQuerySchema = pageQuerySchema.merge(dateRangeQuerySchema).extend({
  status: z3.nativeEnum(PaymentStatus2).optional(),
  kind: z3.nativeEnum(PaymentKind2).optional(),
  /** 주문·묶음 ID prefix / 입금자명(안내·실제) / 구매자 이메일·이름 / 상품 제목 */
  q: z3.string().max(200).optional()
});
var adminPaymentListItemSchema = adminPaymentResponseSchema.extend({
  /** 단건 귀속(추가금 등)이면 주문 id, 묶음 입금이면 null */
  orderId: z3.string().uuid().nullable(),
  orderGroupId: z3.string().uuid().nullable(),
  /** 이 입금이 걸린 주문들 (묶음이면 소속 주문 전체, 주문순) */
  orders: z3.array(z3.object({
    id: z3.string().uuid(),
    status: z3.nativeEnum(OrderStatus2)
  })),
  listingTitle: z3.string().nullable(),
  buyerId: z3.string().uuid().nullable(),
  buyerName: z3.string().nullable(),
  buyerEmail: z3.string().nullable(),
  /** 입금 확인을 처리한 어드민 이름 */
  confirmedByName: z3.string().nullable(),
  failReason: z3.string().nullable(),
  createdAt: z3.string().datetime(),
  /** 마지막 상태 변경 시각 — 만료·취소는 따로 시각이 없어 이것으로 본다 */
  updatedAt: z3.string().datetime()
});
var adminPaymentListResponseSchema = pageResponseSchema(adminPaymentListItemSchema);

// src/admin/settlements.schema.ts
import { RefundStatus as RefundStatus2, SettlementStatus as SettlementStatus2, TerminationRefundMode as TerminationRefundMode2 } from "@inflip/shared";
import { z as z4 } from "zod";
var adminListSettlementsQuerySchema = pageQuerySchema.merge(dateRangeQuerySchema).extend({
  status: z4.nativeEnum(SettlementStatus2).optional(),
  sellerId: z4.string().uuid().optional()
});
var payOutRequestSchema = z4.object({
  note: z4.string().max(500).optional()
});
var holdSettlementRequestSchema = z4.object({
  reason: z4.string().min(1).max(500)
});
var adminSettlementResponseSchema = z4.object({
  id: z4.string().uuid(),
  orderId: z4.string().uuid(),
  sellerId: z4.string().uuid(),
  sellerName: z4.string().optional(),
  /** 지급 목적지 — 판매자가 등록한 정산 계좌 (없으면 지급 보류) */
  sellerPayoutAccount: payoutAccountSchema.nullable().optional(),
  status: z4.nativeEnum(SettlementStatus2),
  grossAmount: z4.number().int(),
  feeAmount: z4.number().int(),
  netAmount: z4.number().int(),
  currency: z4.string(),
  scheduledAt: z4.string().datetime(),
  paidOutAt: z4.string().datetime().nullable(),
  /** 주문이 분쟁 중이면 true — 분쟁이 해결되기 전에는 지급할 수 없다 */
  orderDisputed: z4.boolean().optional(),
  /** 주문이 계약 해지됐으면 선불 처리 — 해지돼도 해지일까지 이행된 대가는 지급한다 */
  orderTermination: z4.object({
    refundMode: z4.nativeEnum(TerminationRefundMode2),
    refundAmount: z4.number().int(),
    refundStatus: z4.nativeEnum(RefundStatus2).nullable()
  }).nullable().optional()
});
var adminSettlementListResponseSchema = pageResponseSchema(adminSettlementResponseSchema);

// src/admin/chat.schema.ts
import { ChatMessageType, ChatParticipantRole, ChatRoomStatus, ChatRoomType, IpRightType as IpRightType2, OrderStatus as OrderStatus3 } from "@inflip/shared";
import { z as z5 } from "zod";
var adminListChatRoomsQuerySchema = pageQuerySchema.extend({
  type: z5.nativeEnum(ChatRoomType).optional(),
  status: z5.nativeEnum(ChatRoomStatus).optional(),
  /** 이 주문의 거래방만 (어드민 주문 상세). */
  orderId: z5.string().uuid().optional(),
  /** 진행 중 분쟁만 보고 싶을 때. */
  orderStatus: z5.nativeEnum(OrderStatus3).optional(),
  /** 어드민이 참여 중인 방만. */
  disputedOnly: z5.union([
    z5.boolean(),
    z5.enum([
      "true",
      "false"
    ])
  ]).transform((v) => typeof v === "boolean" ? v : v === "true").optional(),
  /** 분쟁이 끝났는데 운영자가 나가지 않은 방만. */
  adminStayed: z5.union([
    z5.boolean(),
    z5.enum([
      "true",
      "false"
    ])
  ]).transform((v) => typeof v === "boolean" ? v : v === "true").optional()
});
var adminChatRoomResponseSchema = z5.object({
  id: z5.string().uuid(),
  type: z5.nativeEnum(ChatRoomType),
  /** 그룹 없는 주문(제안 수락)의 거래방만 값. */
  orderId: z5.string().uuid().nullable(),
  /** 체크아웃 주문 묶음의 거래방만 값 — 결제 1건당 방 1개. */
  orderGroupId: z5.string().uuid().nullable(),
  /** 방에 걸린 주문이 정확히 1건일 때만 값 (그룹 내 상태가 갈리면 null). */
  orderStatus: z5.nativeEnum(OrderStatus3).nullable(),
  /** 방에 걸린 주문 수 (일반방은 0). */
  orderCount: z5.number().int().min(0),
  status: z5.nativeEnum(ChatRoomStatus),
  disputeAdminJoinedAt: z5.string().datetime().nullable(),
  buyerId: z5.string().uuid(),
  buyerName: z5.string().nullable(),
  sellerId: z5.string().uuid(),
  sellerName: z5.string().nullable(),
  listingTitle: z5.string().nullable(),
  messageCount: z5.number().int().min(0),
  lastMessageAt: z5.string().datetime().nullable(),
  lastMessagePreview: z5.string().nullable(),
  createdAt: z5.string().datetime()
});
var adminChatParticipantResponseSchema = z5.object({
  id: z5.string().uuid(),
  userId: z5.string().uuid(),
  role: z5.nativeEnum(ChatParticipantRole),
  name: z5.string().nullable(),
  email: z5.string().nullable(),
  joinedAt: z5.string().datetime(),
  leftAt: z5.string().datetime().nullable(),
  lastReadAt: z5.string().datetime().nullable()
});
var adminChatRoomOrderSummarySchema = z5.object({
  id: z5.string().uuid(),
  rightType: z5.nativeEnum(IpRightType2),
  status: z5.nativeEnum(OrderStatus3),
  totalAmount: z5.number().int()
});
var adminChatRoomDetailResponseSchema = adminChatRoomResponseSchema.extend({
  participants: z5.array(adminChatParticipantResponseSchema),
  /** 방에 걸린 주문들 — 그룹방은 주문별 권리/상태를 나열. */
  orders: z5.array(adminChatRoomOrderSummarySchema),
  /** 요청한 어드민 본인이 이 방의 참여자인지 — FE 가 참여/읽기전용 모드를 가른다. */
  viewerJoined: z5.boolean()
});
var adminGetChatMessagesQuerySchema = z5.object({
  beforeId: z5.string().uuid().optional(),
  limit: z5.coerce.number().int().min(1).max(50).default(50)
});
var adminChatMessageResponseSchema = z5.object({
  id: z5.string().uuid(),
  roomId: z5.string().uuid(),
  senderId: z5.string().uuid().nullable(),
  senderName: z5.string().nullable(),
  senderRole: z5.nativeEnum(ChatParticipantRole).nullable(),
  type: z5.nativeEnum(ChatMessageType),
  content: z5.string(),
  attachmentFilename: z5.string().nullable(),
  systemEvent: z5.string().nullable(),
  /** 한 참여자에게만 보이는 SYSTEM 메시지의 역할 (예: 판매자에게만 보이는 발급 지연 안내). 모두에게 보이면 null */
  visibleToRole: z5.nativeEnum(ChatParticipantRole).nullable(),
  createdAt: z5.string().datetime()
});
var adminJoinChatRoomResponseSchema = z5.object({
  ok: z5.literal(true),
  /** 이미 참여자였으면 true (멱등 호출). */
  alreadyJoined: z5.boolean()
});
var adminLeaveChatRoomResponseSchema = z5.object({
  ok: z5.literal(true),
  /** 이미 나가 있었으면 true (멱등 호출). */
  alreadyLeft: z5.boolean()
});

// src/admin/listings.schema.ts
import { IpRightType as IpRightType3, ListingStatus } from "@inflip/shared";
import { z as z6 } from "zod";
var adminListListingsQuerySchema = pageQuerySchema.extend({
  status: z6.nativeEnum(ListingStatus).optional(),
  /** 제목 / 판매자 이름 부분 일치 검색 */
  q: z6.string().trim().min(1).max(100).optional(),
  /** true 면 심사 대기 오퍼(나중에 추가된 권리)가 달린 리스팅만 — 오퍼 심사 큐 */
  pendingOffer: z6.enum([
    "true",
    "false"
  ]).transform((v) => v === "true").optional(),
  /** true 면 한 번이라도 공개됐던 IP 만(판매 재개 심사), false 면 처음 심사만 */
  relisted: z6.enum([
    "true",
    "false"
  ]).transform((v) => v === "true").optional()
});
var AdminOfferReviewKind = {
  NEW: "NEW",
  EDITED: "EDITED",
  EDITED_NO_BASELINE: "EDITED_NO_BASELINE"
};
var adminListingOfferSchema = listingOfferResponseSchema.extend({
  updatedAt: z6.string().datetime(),
  /** 심사 대기 권리만 — 승인된 권리는 null */
  review: z6.object({
    kind: z6.nativeEnum(AdminOfferReviewKind),
    /** 승인 후 수정(EDITED)이면 이전 승인 조건과 달라진 줄만 (판매자 저장 확인 창과 같은 비교) */
    changes: z6.array(z6.object({
      label: z6.string(),
      before: z6.string(),
      after: z6.string()
    }))
  }).nullable()
});
var adminListingResponseSchema = z6.object({
  id: z6.string().uuid(),
  sellerId: z6.string().uuid(),
  sellerName: z6.string(),
  title: z6.string(),
  description: z6.string(),
  usageNotes: z6.string().nullable(),
  status: z6.nativeEnum(ListingStatus),
  priceAmount: z6.number().int(),
  priceCurrency: z6.string(),
  rightTypes: z6.array(z6.nativeEnum(IpRightType3)),
  /** 썸네일 — 없으면 null. 사용자 API 경로(/api/v1 뒤) — 어드민 화면은 사용자 API 주소에 붙여 연다 */
  thumbnailPath: z6.string().nullable(),
  /** 심사 결정에 seenUpdatedAt 으로 그대로 돌려보낸다 */
  offers: z6.array(adminListingOfferSchema),
  publishedAt: z6.string().datetime().nullable(),
  suspendedAt: z6.string().datetime().nullable(),
  suspendedReason: z6.string().nullable(),
  /** 처음 공개된 시각 — 있으면 예전에 판매했던 IP (심사 대기면 판매 재개 심사) */
  firstPublishedAt: z6.string().datetime().nullable(),
  /** 마지막으로 판매 종료한 시각 */
  endedAt: z6.string().datetime().nullable(),
  /** 마지막 판매 중지 — 판매 재개로 지금 중지 표시가 비워져도 남는다 */
  lastSuspendedAt: z6.string().datetime().nullable(),
  lastSuspendedReason: z6.string().nullable(),
  /** 마지막 중지를 어드민이 풀어 준 시각 — 없거나 중지보다 이르면 중지된 채 판매 종료한 것 */
  lastSuspensionLiftedAt: z6.string().datetime().nullable(),
  /** 이 IP 의 주문 수 (취소 제외) */
  orderCount: z6.number().int(),
  /** 지금 이용 중인 라이선스 수 (사용 중지 포함) */
  activeLicenseCount: z6.number().int(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var adminListingListResponseSchema = pageResponseSchema(adminListingResponseSchema);
var rejectListingRequestSchema = seenVersionSchema.extend({
  /** 반려 사유 — 판매자에게 전달 (추후 알림 연동) */
  reason: z6.string().min(1).max(500)
});
var suspendListingRequestSchema = z6.object({
  reason: z6.string().trim().min(5, "\uC911\uC9C0 \uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});
var approveListingRequestSchema = seenVersionSchema;

// src/admin/dashboard.schema.ts
import { z as z7 } from "zod";
var adminDashboardStatsSchema = z7.object({
  /** 처리 대기 큐 */
  paymentPendingCount: z7.number().int(),
  /** 입금 대기 중인 추가금 (주문 상태는 결제 완료 그대로라 입금 확인 대기와 따로 센다) */
  addonPaymentPendingCount: z7.number().int(),
  /** 발급 지연 — 입금 확인 뒤 결제 설정의 기준 일수가 지나도록 발급 전인 주문 */
  licenseIssueDelayedCount: z7.number().int(),
  reviewPendingCount: z7.number().int(),
  /** 판매 중 리스팅에 추가된 심사 대기 오퍼 수 */
  offerReviewPendingCount: z7.number().int(),
  disputedCount: z7.number().int(),
  /** 분쟁이 끝났는데 운영자가 [나가기]를 하지 않은 채팅방 */
  adminStayedChatRoomCount: z7.number().int(),
  refundRequestedCount: z7.number().int(),
  settlementWaitingCount: z7.number().int(),
  /** 송금을 기다리는 입금 반환 (초과·부족·만료 후 입금) */
  depositReturnPendingCount: z7.number().int(),
  /** 기한이 지나도록 판매자가 돌려주지 않은 정산 회수 (판매자 접속 정지 중) */
  payoutClawbackOverdueCount: z7.number().int(),
  /** 고객센터 1:1 문의 답변 대기 */
  supportWaitingCount: z7.number().int(),
  /** 오늘 (서버 로컬 자정 기준) */
  todayOrderCount: z7.number().int(),
  todayConfirmedAmount: z7.number().int(),
  /** 누적 */
  totalUserCount: z7.number().int(),
  publishedListingCount: z7.number().int()
});

// src/admin/users.schema.ts
import { AuthProvider, IpRightType as IpRightType4, ListingStatus as ListingStatus2, OrderStatus as OrderStatus4, SocialPlatform, UserRole } from "@inflip/shared";
import { z as z8 } from "zod";
var AdminUserStatusFilter = {
  ACTIVE: "ACTIVE",
  SUSPENDED: "SUSPENDED",
  /** 탈퇴한 회원(파기 포함) — 전체(상태 없음)에도 나오고, 활성·정지에는 없다 */
  WITHDRAWN: "WITHDRAWN"
};
var adminListUsersQuerySchema = pageQuerySchema.extend({
  /** 이메일/이름 부분 검색 */
  q: z8.string().max(200).optional(),
  role: z8.nativeEnum(UserRole).optional(),
  provider: z8.nativeEnum(AuthProvider).optional(),
  status: z8.nativeEnum(AdminUserStatusFilter).optional(),
  sort: z8.enum([
    "createdAt",
    "lastLoginAt",
    "orderCount"
  ]).default("createdAt")
});
var adminUserResponseSchema = z8.object({
  id: z8.string().uuid(),
  email: z8.string(),
  name: z8.string(),
  avatarUrl: z8.string().nullable(),
  role: z8.nativeEnum(UserRole),
  provider: z8.nativeEnum(AuthProvider),
  emailVerified: z8.boolean(),
  mfaEnrolled: z8.boolean(),
  suspendedAt: z8.string().datetime().nullable(),
  /** 탈퇴 시각 — 탈퇴 회원만 */
  withdrawnAt: z8.string().datetime().nullable().optional(),
  /** 탈퇴 후 보관 기간이 지나 개인정보(이메일·닉네임·연락처·계좌 등)를 지운 시각 — 지운 회원만 */
  anonymizedAt: z8.string().datetime().nullable().optional(),
  lastLoginAt: z8.string().datetime().nullable(),
  createdAt: z8.string().datetime(),
  /** 인증된 SNS 채널 플랫폼 — 1개 이상이면 인증된 인플루언서 */
  verifiedPlatforms: z8.array(z8.nativeEnum(SocialPlatform)),
  /** 활동 요약 */
  orderCount: z8.number().int(),
  listingCount: z8.number().int()
});
var adminUserListResponseSchema = pageResponseSchema(adminUserResponseSchema);
var adminUserOrderSummarySchema = z8.object({
  id: z8.string().uuid(),
  listingTitle: z8.string().nullable(),
  status: z8.nativeEnum(OrderStatus4),
  rightType: z8.nativeEnum(IpRightType4),
  totalAmount: z8.number().int(),
  createdAt: z8.string().datetime()
});
var adminUserListingSummarySchema = z8.object({
  id: z8.string().uuid(),
  title: z8.string(),
  status: z8.nativeEnum(ListingStatus2),
  priceAmount: z8.number().int(),
  createdAt: z8.string().datetime()
});
var adminUserAgreementSchema = z8.object({
  policySlug: z8.string(),
  policyTitle: z8.string(),
  version: z8.number().int(),
  agreedAt: z8.string().datetime()
});
var adminUserVerifiedChannelSchema = z8.object({
  platform: z8.nativeEnum(SocialPlatform),
  username: z8.string().nullable(),
  displayName: z8.string().nullable(),
  profileUrl: z8.string(),
  /** 플랫폼이 숨김 처리하면 null */
  followerCount: z8.number().int().nullable(),
  verifiedAt: z8.string().datetime()
});
var adminUserDetailResponseSchema = adminUserResponseSchema.extend({
  suspendedReason: z8.string().nullable(),
  /** 간편비밀번호 설정 여부 / 잠금 해제 시각 (잠겨 있지 않으면 null) — 고객센터 초기화 판단용 */
  hasSimplePin: z8.boolean(),
  simplePinLockedUntil: z8.string().datetime().nullable(),
  phoneCountryCode: z8.string().nullable(),
  phoneNumber: z8.string().nullable(),
  payoutAccount: z8.object({
    bankName: z8.string(),
    accountNumber: z8.string(),
    holderName: z8.string()
  }).nullable(),
  /** 구매자 환불 계좌 — 환불·입금 반환을 돌려받을 계좌 */
  refundAccount: z8.object({
    bankName: z8.string(),
    accountNumber: z8.string(),
    holderName: z8.string()
  }).nullable(),
  /** 구매 신청(취소되지 않은 구매 주문)이 있는데 환불 계좌가 없다 — 웹이 등록 창을 띄우는 상태 */
  refundAccountRequired: z8.boolean(),
  /** 회원이 직접 입력한 채널 링크 (미인증 포함) */
  socialLinks: z8.record(z8.string()).nullable(),
  verifiedChannels: z8.array(adminUserVerifiedChannelSchema),
  /** 구매 총액 (완료·진행 포함, CANCELLED 제외) */
  totalPurchaseAmount: z8.number().int(),
  recentOrders: z8.array(adminUserOrderSummarySchema),
  recentListings: z8.array(adminUserListingSummarySchema),
  agreements: z8.array(adminUserAgreementSchema),
  /** 운영 계정 — 어드민이 이 회원으로 웹에 들어갈 수 있다. 사용자 API 에는 없는 값 */
  isOperatorAccount: z8.boolean(),
  operatorMemo: z8.string().nullable()
});
var operatorSignInResponseSchema = z8.object({
  url: z8.string().url(),
  expiresAt: z8.string().datetime()
});
var suspendUserRequestSchema = z8.object({
  /** 정지 사유 — 감사 로그/상세 화면에 남는다 */
  reason: z8.string().min(2).max(500)
});
var unsuspendUserRequestSchema = z8.object({
  seenSuspendedAt: z8.string().datetime(),
  seenSuspendedReason: z8.string().nullable()
});
var revokeSocialChannelRequestSchema = z8.object({
  /** 해제 사유 — 회원 알림과 감사 로그에 남는다 */
  reason: z8.string().trim().min(2, "\uD574\uC81C \uC0AC\uC720\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(500),
  /** 화면에서 본 채널의 인증 시각 — 그사이 회원이 다른 계정으로 다시 인증했으면 거절한다 */
  verifiedAt: z8.string().datetime()
});

// src/admin/audit-logs.schema.ts
import { z as z9 } from "zod";
var adminListAuditLogsQuerySchema = pageQuerySchema.extend({
  actorId: z9.string().uuid().optional(),
  /** action 부분 검색 (예: "confirm-payment") 또는 대상 id 정확히 일치 */
  q: z9.string().max(200).optional()
});
var adminAuditLogResponseSchema = z9.object({
  id: z9.string().uuid(),
  actorId: z9.string().uuid(),
  actorRole: z9.string(),
  action: z9.string(),
  targetType: z9.string(),
  targetId: z9.string().nullable(),
  payload: z9.record(z9.unknown()).nullable(),
  ip: z9.string().nullable(),
  createdAt: z9.string().datetime()
});

// src/admin/ledger.schema.ts
import { LedgerCounterparty, LedgerEntryType } from "@inflip/shared";
import { z as z10 } from "zod";
var adminLedgerEntryResponseSchema = z10.object({
  id: z10.string().uuid(),
  orderId: z10.string().uuid(),
  type: z10.nativeEnum(LedgerEntryType),
  amount: z10.number().int(),
  currency: z10.string(),
  counterparty: z10.nativeEnum(LedgerCounterparty),
  referenceId: z10.string().nullable(),
  memo: z10.string().nullable(),
  recordedBy: z10.string().nullable(),
  createdAt: z10.string().datetime()
});

// src/admin/royalties.schema.ts
import { IpRightType as IpRightType5, RoyaltyReportStatus } from "@inflip/shared";
import { z as z11 } from "zod";
var adminListRoyaltyReportsQuerySchema = pageQuerySchema.merge(dateRangeQuerySchema).extend({
  status: z11.nativeEnum(RoyaltyReportStatus).optional(),
  /** 주문 번호 앞자리, 상품 이름, 구매자·판매자 이름·이메일 */
  q: z11.string().trim().max(100).optional(),
  /** true 면 판매자 이의가 열려 있는 보고만 */
  openObjection: z11.enum([
    "true",
    "false"
  ]).transform((v) => v === "true").optional()
});
var adminRoyaltyReportResponseSchema = royaltyReportResponseSchema.extend({
  sellerName: z11.string().optional(),
  sellerPayoutAccount: payoutAccountSchema.nullable().optional(),
  /** 로열티를 입금하는 쪽 — 주문의 구매자 */
  buyerId: z11.string().uuid(),
  buyerName: z11.string(),
  buyerEmail: z11.string(),
  /** 로열티를 지급받는 쪽 — 주문의 판매자 */
  sellerId: z11.string().uuid(),
  sellerEmail: z11.string(),
  /** 어떤 거래의 로열티인지 */
  listingTitle: z11.string().nullable(),
  rightType: z11.nativeEnum(IpRightType5),
  /** 구매자에게 안내한 입금자명 — 주문 결제 때의 입금자명 (로열티 입금 대사 기준) */
  buyerDepositorName: z11.string().nullable()
});
var adminRoyaltyReportListResponseSchema = pageResponseSchema(adminRoyaltyReportResponseSchema);
var adminRoyaltyReportCountsResponseSchema = z11.object({
  /** 입금 확인 대기 */
  reported: z11.number().int(),
  /** 지급 대기 */
  confirmed: z11.number().int(),
  /** 판매자 이의가 열린 보고 */
  openObjection: z11.number().int()
});
var voidRoyaltyReportRequestSchema = z11.object({
  reason: z11.string().trim().min(5, "\uBC18\uB824 \uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});
var adjustRoyaltyReportRequestSchema = z11.object({
  correctedRevenue: z11.number().int().min(0).max(1e12),
  reason: z11.string().trim().min(5, "\uC815\uC815 \uADFC\uAC70\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});
var dismissRoyaltyObjectionRequestSchema = z11.object({
  reason: z11.string().trim().min(5, "\uAE30\uAC01 \uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});
var confirmRoyaltyDepositRequestSchema = z11.object({
  reportIds: z11.array(z11.string().uuid()).min(1, "\uC785\uAE08 \uD655\uC778\uD560 \uBCF4\uACE0\uB97C \uACE8\uB77C \uC8FC\uC138\uC694").max(100),
  receivedAmount: z11.number().int().positive(),
  depositorName: z11.string().trim().min(1, "\uC740\uD589 \uB0B4\uC5ED\uC758 \uC785\uAE08\uC790\uBA85\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(64),
  depositorOverrideReason: z11.string().trim().min(5, "\uD655\uC778\uD55C \uADFC\uAC70\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500).optional()
});

// src/admin/policies.schema.ts
import { PolicyLanguage, PolicyVersionStatus } from "@inflip/shared";
import { z as z12 } from "zod";
var createPolicyRequestSchema = z12.object({
  slug: z12.string().min(2).max(128).regex(/^[a-z0-9-]+$/, "\uC18C\uBB38\uC790/\uC22B\uC790/\uD558\uC774\uD508\uB9CC"),
  title: z12.string().min(1).max(128),
  description: z12.string().max(512).optional(),
  isRequired: z12.boolean().default(false),
  /** false 면 동의 없이 게시만 하는 문서 (개인정보 처리방침 등) — 필수 동의일 수 없다 */
  needsConsent: z12.boolean().default(true),
  sortOrder: z12.number().int().min(0).default(0)
});
var updatePolicyRequestSchema = createPolicyRequestSchema.omit({
  slug: true
}).partial().extend({
  isActive: z12.boolean().optional()
});
var policyVersionSummarySchema = z12.object({
  id: z12.string().uuid(),
  version: z12.number().int(),
  status: z12.nativeEnum(PolicyVersionStatus),
  /** 한국어 제목 (없으면 빈 값) */
  title: z12.string(),
  /** 제목·본문이 채워진 언어 — 언어 탭의 ● */
  languages: z12.array(z12.nativeEnum(PolicyLanguage)),
  publishedAt: z12.string().datetime().nullable(),
  /** 시행일 — 이날부터 회원은 동의해야 계속 쓴다 (게시 전 초안은 null) */
  effectiveAt: z12.string().datetime().nullable(),
  /** 발행본 오타 수정 가능 기한 (발행 +7일). 초안은 null(항상 수정 가능) */
  editableUntil: z12.string().datetime().nullable(),
  updatedAt: z12.string().datetime()
});
var policyVersionContentSchema = z12.object({
  language: z12.nativeEnum(PolicyLanguage),
  title: z12.string(),
  /** 본문 HTML (허용 태그만) */
  content: z12.string()
});
var policyVersionDetailSchema = policyVersionSummarySchema.extend({
  /** 저장된 언어만 — 없는 언어는 빈 탭 */
  contents: z12.array(policyVersionContentSchema)
});
var adminPolicySchema = z12.object({
  id: z12.string().uuid(),
  slug: z12.string(),
  title: z12.string(),
  description: z12.string().nullable(),
  isRequired: z12.boolean(),
  needsConsent: z12.boolean(),
  sortOrder: z12.number().int(),
  isActive: z12.boolean(),
  /** 현재 발행 중인 버전 번호 (없으면 null) */
  publishedVersion: z12.number().int().nullable(),
  publishedAt: z12.string().datetime().nullable()
});
var adminPolicyDetailSchema = adminPolicySchema.extend({
  versions: z12.array(policyVersionSummarySchema)
});
var upsertPolicyContentRequestSchema = z12.object({
  title: z12.string().trim().min(1).max(256),
  content: z12.string().max(2e5)
});
var publishPolicyVersionRequestSchema = z12.object({
  effectiveAt: z12.string().datetime().optional()
});

// src/admin/admins.schema.ts
import { UserRole as UserRole2 } from "@inflip/shared";
import { z as z13 } from "zod";
var createAdminRequestSchema = z13.object({
  email: z13.string().trim().toLowerCase().email().max(320),
  name: z13.string().trim().min(1).max(100),
  password: z13.string().min(10, "\uC5B4\uB4DC\uBBFC \uBE44\uBC00\uBC88\uD638\uB294 10\uC790 \uC774\uC0C1").max(128),
  role: z13.enum([
    UserRole2.ADMIN,
    UserRole2.SUPER_ADMIN
  ]).default(UserRole2.ADMIN)
});
var resetAdminPasswordRequestSchema = z13.object({
  password: z13.string().min(10).max(128)
});
var adminAccountSchema = z13.object({
  id: z13.string().uuid(),
  email: z13.string(),
  name: z13.string(),
  role: z13.nativeEnum(UserRole2),
  mfaEnrolled: z13.boolean(),
  /** 계정 시간대 (IANA) — 어드민 화면의 시각 표시·날짜 필터 기준 */
  timeZone: z13.string(),
  createdAt: z13.string().datetime()
});
var updateMyProfileRequestSchema = z13.object({
  name: z13.string().trim().min(1).max(100)
});
var changeMyPasswordRequestSchema = z13.object({
  currentPassword: z13.string().min(1).max(128),
  newPassword: z13.string().min(10, "\uC5B4\uB4DC\uBBFC \uBE44\uBC00\uBC88\uD638\uB294 10\uC790 \uC774\uC0C1").max(128)
});

// src/admin/security.schema.ts
import { z as z14 } from "zod";
var otpCodeSchema = z14.string().regex(/^\d{6}$/, "OTP \uCF54\uB4DC\uB294 6\uC790\uB9AC \uC22B\uC790\uC608\uC694");
var adminSecuritySettingsSchema = z14.object({
  /** 실제로 적용 중인지 — 서버 설정이 켜져 있으면 저장값과 상관없이 true */
  adminMfaEnabled: z14.boolean(),
  /** 화면에서 저장한 값 */
  adminMfaSaved: z14.boolean(),
  /** 서버 설정(ADMIN_MFA_ENABLED)으로 항상 켜져 있음 — 화면에서 끌 수 없다 */
  adminMfaForcedByServer: z14.boolean(),
  /** 내 OTP 등록 상태 — 대기는 키를 받았지만 첫 코드 확인 전 */
  myMfa: z14.enum([
    "NONE",
    "PENDING",
    "ENROLLED"
  ]),
  /** 설정을 바꿀 수 있는지 (슈퍼어드민만) */
  canManage: z14.boolean()
});
var updateAdminMfaSettingRequestSchema = z14.object({
  enabled: z14.boolean(),
  code: otpCodeSchema
});
var myMfaSetupResponseSchema = z14.object({
  /** base32 secret — OTP 앱에 수동 입력용 */
  secret: z14.string(),
  /** otpauth:// URI — QR 코드용 */
  otpauthUrl: z14.string()
});
var confirmMyMfaRequestSchema = z14.object({
  code: otpCodeSchema
});

// src/admin/social-channel-requests.schema.ts
import { z as z15 } from "zod";
var listSocialChannelRequestsQuerySchema = pageQuerySchema.extend({
  status: socialChannelRequestStatusSchema.optional()
});
var adminSocialChannelRequestSchema = z15.object({
  id: z15.string().uuid(),
  userId: z15.string().uuid(),
  userEmail: z15.string(),
  userName: z15.string(),
  platform: manualSocialPlatformSchema,
  url: z15.string(),
  status: socialChannelRequestStatusSchema,
  /** 승인 시 어드민이 입력한 팔로워 수 */
  followerCount: z15.number().int().nullable(),
  rejectReason: z15.string().nullable(),
  createdAt: z15.string().datetime(),
  processedAt: z15.string().datetime().nullable(),
  /** 심사 결정에 seenUpdatedAt 으로 그대로 돌려보낸다 — 다시 요청하면 같은 행이 바뀐다 */
  updatedAt: z15.string().datetime()
});
var adminSocialChannelRequestPageSchema = pageResponseSchema(adminSocialChannelRequestSchema);
var approveSocialChannelRequestSchema = seenVersionSchema.extend({
  /** 어드민이 채널에서 직접 확인한 팔로워 수 */
  followerCount: z15.coerce.number().int().nonnegative()
});
var rejectSocialChannelRequestSchema = seenVersionSchema.extend({
  reason: z15.string().trim().min(1, "\uAC70\uC808 \uC0AC\uC720\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694.").max(300)
});

// src/admin/sponsorships.schema.ts
import { SponsorshipApplicationStatus, SponsorshipApprovalMode, SponsorshipRegion, SponsorshipStatus } from "@inflip/shared";
import { z as z16 } from "zod";
var adminListSponsorshipsQuerySchema = pageQuerySchema.extend({
  status: z16.nativeEnum(SponsorshipStatus).optional(),
  /** 협찬 제목·업체명 부분 일치 */
  q: z16.string().trim().max(100).optional(),
  /** 회원 상세에서 그 회원이 등록한 협찬만 */
  ownerId: z16.string().uuid().optional()
});
var adminSponsorshipApprovedSchema = z16.object({
  title: z16.string(),
  businessName: z16.string(),
  description: z16.string(),
  benefit: z16.string(),
  categoryId: z16.string(),
  /** 종목 이름 — 어드민이 종목을 지웠으면 null */
  categoryLabel: z16.string().nullable(),
  region: z16.nativeEnum(SponsorshipRegion),
  location: z16.string().nullable(),
  approvalMode: z16.nativeEnum(SponsorshipApprovalMode),
  requirements: sponsorshipRequirementsSchema,
  /** 이 항목이 생기기 전 승인본에는 없다 */
  visitRequired: z16.boolean().optional()
});
var adminSponsorshipResponseSchema = sponsorshipResponseSchema.extend({
  ownerEmail: z16.string(),
  /** 마지막으로 승인된 내용 — 한 번도 승인된 적 없으면 null */
  approved: adminSponsorshipApprovedSchema.nullable(),
  updatedAt: z16.string().datetime()
});
var adminSponsorshipPageSchema = pageResponseSchema(adminSponsorshipResponseSchema);
var rejectSponsorshipRequestSchema = seenVersionSchema.extend({
  /** 등록자에게 그대로 전달되는 반려 사유 */
  reason: z16.string().trim().min(2, "\uBC18\uB824 \uC0AC\uC720\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(500)
});
var adminListSponsorshipApplicationsQuerySchema = pageQuerySchema.extend({
  status: z16.nativeEnum(SponsorshipApplicationStatus).optional(),
  /** 회원 상세에서 그 회원의 지원만 볼 때 */
  applicantId: z16.string().uuid().optional(),
  /** 한 협찬에 들어온 지원만 볼 때 (회원 상세의 등록한 협찬) */
  sponsorshipId: z16.string().uuid().optional(),
  /** 지원자 이름·이메일, 협찬 제목·업체명, 협찬 작성자 이름·이메일 부분 일치 */
  q: z16.string().trim().max(50).optional()
});
var adminSponsorshipApplicationSchema = z16.object({
  id: z16.string().uuid(),
  sponsorshipId: z16.string().uuid(),
  sponsorshipTitle: z16.string(),
  businessName: z16.string(),
  sponsorshipStatus: z16.nativeEnum(SponsorshipStatus),
  /** 협찬이 삭제됐으면 true (지원 기록은 남는다) */
  sponsorshipDeleted: z16.boolean(),
  /** 협찬을 등록한 회원 */
  ownerId: z16.string().uuid(),
  ownerName: z16.string(),
  ownerEmail: z16.string(),
  applicantId: z16.string().uuid(),
  applicantName: z16.string(),
  applicantEmail: z16.string(),
  status: z16.nativeEnum(SponsorshipApplicationStatus),
  message: z16.string().nullable(),
  /** 지원 시점에 조건을 통과시킨 채널과 그때의 구독자·팔로워 수 */
  qualifiedPlatform: socialPlatformSchema.nullable(),
  qualifiedFollowerCount: z16.number().int().nullable(),
  visitDate: z16.string().nullable(),
  decidedAt: z16.string().datetime().nullable(),
  /** 거절 사유 — 거절된 지원만 (사유를 받기 전에 거절된 지원은 null) */
  rejectReason: z16.string().nullable(),
  createdAt: z16.string().datetime()
});
var adminSponsorshipApplicationPageSchema = pageResponseSchema(adminSponsorshipApplicationSchema);
var takeDownSponsorshipRequestSchema = z16.object({
  reason: z16.string().trim().min(2, "\uC0AC\uC720\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(500)
});
var approveSponsorshipRequestSchema = seenVersionSchema;

// src/admin/reports.schema.ts
import { ChatReportReason, ChatRoomType as ChatRoomType2, ReportStatus } from "@inflip/shared";
import { z as z17 } from "zod";
var listReportsQuerySchema = pageQuerySchema.extend({
  status: z17.nativeEnum(ReportStatus).default(ReportStatus.PENDING)
});
var reportUserSchema = z17.object({
  id: z17.string().uuid(),
  name: z17.string().nullable(),
  email: z17.string().nullable()
});
var adminCommunityReportItemSchema = z17.object({
  id: z17.string().uuid(),
  reporter: reportUserSchema,
  /** COMMUNITY_REPORT_REASONS 의 키 */
  reason: z17.string(),
  detail: z17.string().nullable(),
  status: z17.nativeEnum(ReportStatus),
  createdAt: z17.string().datetime()
});
var adminCommunityReportGroupSchema = z17.object({
  targetType: z17.enum([
    "POST",
    "COMMENT"
  ]),
  targetId: z17.string().uuid(),
  postId: z17.string().uuid(),
  postTitle: z17.string(),
  /** 가장 최근 신고 때의 내용 */
  contentSnapshot: z17.string(),
  /** 지금 내용 — 삭제됐으면 null */
  currentContent: z17.string().nullable(),
  deleted: z17.boolean(),
  author: reportUserSchema.nullable(),
  reportCount: z17.number().int(),
  reports: z17.array(adminCommunityReportItemSchema),
  lastReportedAt: z17.string().datetime(),
  resolvedAt: z17.string().datetime().nullable(),
  resolutionNote: z17.string().nullable()
});
var adminCommunityReportPageSchema = pageResponseSchema(adminCommunityReportGroupSchema);
var resolveCommunityReportRequestSchema = z17.object({
  note: z17.string().trim().min(2, "\uCC98\uB9AC \uC0AC\uC720\uB97C 2\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});
var adminChatReportSchema = z17.object({
  id: z17.string().uuid(),
  reporter: reportUserSchema,
  targetUser: reportUserSchema,
  roomId: z17.string().uuid(),
  roomType: z17.nativeEnum(ChatRoomType2),
  reason: z17.nativeEnum(ChatReportReason),
  detail: z17.string().nullable(),
  status: z17.nativeEnum(ReportStatus),
  createdAt: z17.string().datetime(),
  resolvedAt: z17.string().datetime().nullable(),
  resolutionNote: z17.string().nullable()
});
var adminChatReportPageSchema = pageResponseSchema(adminChatReportSchema);
var resolveChatReportRequestSchema = z17.object({
  status: z17.enum([
    ReportStatus.ACTIONED,
    ReportStatus.DISMISSED
  ]),
  note: z17.string().trim().min(2, "\uCC98\uB9AC \uB0B4\uC6A9\uC744 2\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});
var reportPendingCountSchema = z17.object({
  community: z17.number().int(),
  chat: z17.number().int()
});

// src/admin/community.schema.ts
import { CommunityPostCategory } from "@inflip/shared";
import { z as z18 } from "zod";
var listAdminCommunityPostsQuerySchema = pageQuerySchema.extend({
  /** 제목·본문 검색 */
  q: z18.string().trim().max(50).optional(),
  status: z18.enum([
    "ACTIVE",
    "DELETED",
    "ALL"
  ]).default("ACTIVE")
});
var communityAuthorSchema = z18.object({
  id: z18.string().uuid(),
  /** 탈퇴 등으로 찾을 수 없으면 null */
  name: z18.string().nullable(),
  email: z18.string().nullable()
});
var adminCommunityPostSummarySchema = z18.object({
  id: z18.string().uuid(),
  /** 말머리 — 일반·질문·정보 */
  category: z18.nativeEnum(CommunityPostCategory),
  title: z18.string(),
  author: communityAuthorSchema,
  commentCount: z18.number().int(),
  likeCount: z18.number().int(),
  /** 검토 대기 신고 수 (글 자체에 들어온 것) */
  pendingReportCount: z18.number().int(),
  createdAt: z18.string().datetime(),
  deletedAt: z18.string().datetime().nullable()
});
var adminCommunityPostPageSchema = pageResponseSchema(adminCommunityPostSummarySchema);
var adminCommunityCommentSchema = z18.object({
  id: z18.string().uuid(),
  parentCommentId: z18.string().uuid().nullable(),
  replyToCommentId: z18.string().uuid().nullable(),
  content: z18.string(),
  author: communityAuthorSchema,
  isEdited: z18.boolean(),
  pendingReportCount: z18.number().int(),
  createdAt: z18.string().datetime(),
  deletedAt: z18.string().datetime().nullable()
});
var adminCommunityPostDetailSchema = adminCommunityPostSummarySchema.extend({
  content: z18.string(),
  /** 삭제된 댓글도 함께 — 작성 순 */
  comments: z18.array(adminCommunityCommentSchema)
});
var removeCommunityContentRequestSchema = z18.object({
  note: z18.string().trim().min(2, "\uC0AD\uC81C \uC0AC\uC720\uB97C 2\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});

// src/admin/sales.schema.ts
import { isValidDateOnly } from "@inflip/shared";
import { z as z19 } from "zod";
var dateOnly2 = z19.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD").refine(isValidDateOnly, "\uC5C6\uB294 \uB0A0\uC9DC\uC608\uC694");
var SalesEntryKind = {
  /** 주문 결제 확인 */
  PAYMENT: "PAYMENT",
  /** 추가금 입금 확인 */
  ADDON: "ADDON",
  /** 환불 지급 — 그 주문에 받은 금액 전부 */
  FULL_REFUND: "FULL_REFUND",
  /** 환불 지급 — 받은 금액 일부 */
  PARTIAL_REFUND: "PARTIAL_REFUND",
  /** 로열티 입금 확인 */
  ROYALTY: "ROYALTY",
  /** 로열티 정정 환급 (과다 납부분을 돌려줌) */
  ROYALTY_REFUND: "ROYALTY_REFUND"
};
var adminSalesQuerySchema = z19.object({
  dateFrom: dateOnly2.optional(),
  dateTo: dateOnly2.optional(),
  /** 묶음 단위 — 일별 / 월별 */
  groupBy: z19.enum([
    "day",
    "month"
  ]).default("day")
});
var adminSalesTotalsSchema = z19.object({
  /** 주문 결제(추가금 포함) */
  paymentCount: z19.number().int(),
  addonCount: z19.number().int(),
  paymentAmount: z19.number().int(),
  fullRefundCount: z19.number().int(),
  partialRefundCount: z19.number().int(),
  refundAmount: z19.number().int(),
  /** 결제 − 환불 */
  netAmount: z19.number().int(),
  /** 플랫폼 수수료 — 주문에 고정된 요율. 환불분 수수료는 뺀다 */
  feeAmount: z19.number().int(),
  /** 로열티 — 입금 확인·정정 환급 */
  royaltyCount: z19.number().int(),
  royaltyAmount: z19.number().int(),
  royaltyRefundCount: z19.number().int(),
  royaltyRefundAmount: z19.number().int(),
  royaltyNetAmount: z19.number().int(),
  /** 로열티 수수료 — 보고 금액 − 판매자 몫. 환급분은 뺀다 */
  royaltyFeeAmount: z19.number().int()
});
var adminSalesSummarySchema = z19.object({
  dateFrom: dateOnly2,
  dateTo: dateOnly2,
  /** 날짜를 나눈 시간대 (어드민 계정) */
  timeZone: z19.string(),
  totals: adminSalesTotalsSchema,
  /** 일별(YYYY-MM-DD) 또는 월별(YYYY-MM) — 매출이 있는 칸만, 최근순 */
  buckets: z19.array(adminSalesTotalsSchema.extend({
    key: z19.string()
  }))
});
var adminSalesEntriesQuerySchema = pageQuerySchema.extend({
  dateFrom: dateOnly2.optional(),
  dateTo: dateOnly2.optional(),
  kind: z19.nativeEnum(SalesEntryKind).optional()
});
var adminSalesEntrySchema = z19.object({
  id: z19.string().uuid(),
  at: z19.string().datetime(),
  kind: z19.nativeEnum(SalesEntryKind),
  amount: z19.number().int(),
  fee: z19.number().int(),
  orderId: z19.string().uuid(),
  orderGroupId: z19.string().uuid().nullable(),
  listingTitle: z19.string().nullable(),
  buyerId: z19.string().uuid(),
  buyerName: z19.string().nullable(),
  sellerName: z19.string().nullable(),
  memo: z19.string().nullable()
});
var adminSalesEntriesResponseSchema = pageResponseSchema(adminSalesEntrySchema);

// src/admin/proposals.schema.ts
import { IpRightType as IpRightType6, ProposalStatus } from "@inflip/shared";
import { z as z20 } from "zod";
var adminListProposalsQuerySchema = pageQuerySchema.extend({
  status: z20.nativeEnum(ProposalStatus).optional(),
  q: z20.string().trim().max(200).optional()
});
var adminProposalPartySchema = z20.object({
  id: z20.string().uuid(),
  name: z20.string().nullable(),
  email: z20.string().nullable()
});
var adminProposalListItemSchema = z20.object({
  id: z20.string().uuid(),
  status: z20.nativeEnum(ProposalStatus),
  listingId: z20.string().uuid(),
  listingTitle: z20.string().nullable(),
  rightType: z20.nativeEnum(IpRightType6),
  buyer: adminProposalPartySchema,
  seller: adminProposalPartySchema,
  /** 마지막 제안자 — 그 상대가 응답할 차례 */
  proposedBy: z20.string().uuid(),
  round: z20.number().int(),
  terms: contractTermsSchema,
  message: z20.string().nullable(),
  /** 응답 기한 — 진행 중일 때만 */
  expiresAt: z20.string().datetime().nullable(),
  decidedAt: z20.string().datetime().nullable(),
  /** 끝낸 사람 — 거절한 상대·취소한 제안자·종료한 어드민 */
  closedBy: z20.string().uuid().nullable(),
  closeReason: z20.string().nullable(),
  orderId: z20.string().uuid().nullable(),
  history: adminProposalHistorySchema,
  createdAt: z20.string().datetime(),
  updatedAt: z20.string().datetime()
});
var adminProposalListResponseSchema = pageResponseSchema(adminProposalListItemSchema);
var closeProposalRequestSchema = z20.object({
  reason: z20.string().trim().min(5, "\uC885\uB8CC \uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(1e3)
});

// src/admin/support.schema.ts
import { SupportInquiryCategory, SupportInquiryStatus, SupportMessageAuthor, OrderStatus as OrderStatus5 } from "@inflip/shared";
import { z as z21 } from "zod";
var listSupportInquiriesQuerySchema = pageQuerySchema.extend({
  /** 비우면 전체 */
  status: z21.nativeEnum(SupportInquiryStatus).optional(),
  category: z21.nativeEnum(SupportInquiryCategory).optional(),
  /** 제목·회원 이름·이메일 */
  q: z21.string().trim().max(100).optional(),
  /** 이 주문의 문의만 — 주문 상세에서 */
  orderId: z21.string().uuid().optional()
});
var supportUserSchema = z21.object({
  id: z21.string().uuid(),
  name: z21.string().nullable(),
  email: z21.string().nullable()
});
var supportOrderSchema = z21.object({
  id: z21.string().uuid(),
  listingTitle: z21.string().nullable(),
  status: z21.nativeEnum(OrderStatus5),
  /** 문의한 회원이 이 주문의 구매자인지 판매자인지 */
  role: z21.enum([
    "buyer",
    "seller"
  ])
});
var adminSupportInquirySchema = z21.object({
  id: z21.string().uuid(),
  category: z21.nativeEnum(SupportInquiryCategory),
  title: z21.string(),
  status: z21.nativeEnum(SupportInquiryStatus),
  user: supportUserSchema,
  order: supportOrderSchema.nullable(),
  lastMessageAt: z21.string().datetime(),
  answeredAt: z21.string().datetime().nullable(),
  closedAt: z21.string().datetime().nullable(),
  /** 회원이 지운 시각 — 회원 화면에서는 사라졌다 */
  userDeletedAt: z21.string().datetime().nullable(),
  createdAt: z21.string().datetime()
});
var adminSupportInquiryPageSchema = pageResponseSchema(adminSupportInquirySchema);
var adminSupportMessageSchema = z21.object({
  id: z21.string().uuid(),
  author: z21.nativeEnum(SupportMessageAuthor),
  /** 회원 이름 또는 답한 관리자 이름 */
  authorName: z21.string().nullable(),
  body: z21.string(),
  createdAt: z21.string().datetime()
});
var adminSupportInquiryDetailSchema = adminSupportInquirySchema.extend({
  messages: z21.array(adminSupportMessageSchema)
});

// src/admin/scheduler-jobs.schema.ts
import { SCHEDULER_JOB_KEYS, SchedulerJobStatus } from "@inflip/shared";
import { z as z22 } from "zod";
var schedulerJobKeySchema = z22.enum(SCHEDULER_JOB_KEYS);
var schedulerJobResponseSchema = z22.object({
  key: schedulerJobKeySchema,
  /** 화면에 보이는 작업 이름 */
  title: z22.string(),
  /** 주기 (예: 매시 정각) */
  scheduleLabel: z22.string(),
  /** 꺼지면 정해진 주기에 건너뛴다 — [지금 실행]은 꺼져 있어도 돈다 */
  enabled: z22.boolean(),
  lastStartedAt: z22.string().datetime().nullable(),
  lastFinishedAt: z22.string().datetime().nullable(),
  /** 한 번도 안 돌았으면 null */
  lastStatus: z22.nativeEnum(SchedulerJobStatus).nullable(),
  /** 마지막 실패의 오류 메시지 (짧게 자름) */
  lastError: z22.string().nullable(),
  lastDurationMs: z22.number().int().nullable(),
  /** [지금 실행] 요청 시각 — 스케줄러가 집어 가기 전까지 남아 있다 (대기 중) */
  runRequestedAt: z22.string().datetime().nullable()
});
var updateSchedulerJobRequestSchema = z22.object({
  enabled: z22.boolean()
});
export {
  AdminOfferReviewKind,
  AdminUserStatusFilter,
  DEFAULT_PLATFORM_SETTINGS,
  LISTING_RIGHTS_LIMIT,
  PLATFORM_ASSET_KEYS,
  PLATFORM_ASSET_RULES,
  PLATFORM_SETTINGS_KEY,
  POLICY_COMPANY_PLACEHOLDERS,
  SOCIAL_LINK_KEYS,
  SOCIAL_LINK_LABELS,
  SOCIAL_LINK_LABELS_I18N,
  SalesEntryKind,
  adjustRoyaltyReportRequestSchema,
  adminAccountSchema,
  adminAuditLogResponseSchema,
  adminChatMessageResponseSchema,
  adminChatParticipantResponseSchema,
  adminChatReportPageSchema,
  adminChatReportSchema,
  adminChatRoomDetailResponseSchema,
  adminChatRoomOrderSummarySchema,
  adminChatRoomResponseSchema,
  adminCommunityCommentSchema,
  adminCommunityPostDetailSchema,
  adminCommunityPostPageSchema,
  adminCommunityPostSummarySchema,
  adminCommunityReportGroupSchema,
  adminCommunityReportItemSchema,
  adminCommunityReportPageSchema,
  adminDashboardStatsSchema,
  adminDepositReturnListItemSchema,
  adminDepositReturnListResponseSchema,
  adminDepositReturnSchema,
  adminDisputeEvidenceQuerySchema,
  adminDisputeEvidenceResponseSchema,
  adminGetChatMessagesQuerySchema,
  adminJoinChatRoomResponseSchema,
  adminLeaveChatRoomResponseSchema,
  adminLedgerEntryResponseSchema,
  adminLicenseDraftSchema,
  adminListAuditLogsQuerySchema,
  adminListChatRoomsQuerySchema,
  adminListDepositReturnsQuerySchema,
  adminListListingsQuerySchema,
  adminListOrdersQuerySchema,
  adminListPaymentsQuerySchema,
  adminListProposalsQuerySchema,
  adminListRefundsQuerySchema,
  adminListRoyaltyReportsQuerySchema,
  adminListSettlementsQuerySchema,
  adminListSponsorshipApplicationsQuerySchema,
  adminListSponsorshipsQuerySchema,
  adminListUsersQuerySchema,
  adminListingListResponseSchema,
  adminListingOfferSchema,
  adminListingResponseSchema,
  adminMfaEnrollmentResponseSchema,
  adminOrderLicenseSchema,
  adminOrderListResponseSchema,
  adminOrderResponseSchema,
  adminOrderTerminationSchema,
  adminOrderTodoCountsSchema,
  adminPastDisputeSchema,
  adminPaymentListItemSchema,
  adminPaymentListResponseSchema,
  adminPaymentResponseSchema,
  adminPayoutClawbackSchema,
  adminPolicyDetailSchema,
  adminPolicySchema,
  adminProposalHistorySchema,
  adminProposalListItemSchema,
  adminProposalListResponseSchema,
  adminRefreshRequestSchema,
  adminRefundListItemSchema,
  adminRefundListResponseSchema,
  adminRefundResponseSchema,
  adminReviveCheckSchema,
  adminRoyaltyReportCountsResponseSchema,
  adminRoyaltyReportListResponseSchema,
  adminRoyaltyReportResponseSchema,
  adminSalesEntriesQuerySchema,
  adminSalesEntriesResponseSchema,
  adminSalesEntrySchema,
  adminSalesQuerySchema,
  adminSalesSummarySchema,
  adminSalesTotalsSchema,
  adminSecuritySettingsSchema,
  adminSettlementListResponseSchema,
  adminSettlementResponseSchema,
  adminSignInRequestSchema,
  adminSignInResponseSchema,
  adminSocialChannelRequestPageSchema,
  adminSocialChannelRequestSchema,
  adminSponsorshipApplicationPageSchema,
  adminSponsorshipApplicationSchema,
  adminSponsorshipApprovedSchema,
  adminSponsorshipPageSchema,
  adminSponsorshipResponseSchema,
  adminSupportInquiryDetailSchema,
  adminSupportInquiryPageSchema,
  adminSupportInquirySchema,
  adminSupportMessageSchema,
  supportInquiryReplyRequestSchema as adminSupportReplyRequestSchema,
  adminTerminationPreviewSchema,
  adminTokensResponseSchema,
  adminUserAgreementSchema,
  adminUserDetailResponseSchema,
  adminUserListResponseSchema,
  adminUserListingSummarySchema,
  adminUserOrderSummarySchema,
  adminUserResponseSchema,
  adminUserVerifiedChannelSchema,
  adminViolationReportSchema,
  appSemverSchema,
  appVersionResponseSchema,
  approveListingRequestSchema,
  approveSocialChannelRequestSchema,
  approveSponsorshipRequestSchema,
  cancelOrderRequestSchema,
  cancelTerminationRequestSchema,
  catalogOptionSchema,
  changeMyPasswordRequestSchema,
  channelMinFollowersSchema,
  closeProposalRequestSchema,
  collectPayoutClawbackRequestSchema,
  compareAppVersions,
  completeDepositReturnRequestSchema,
  confirmMyMfaRequestSchema,
  confirmPaymentRequestSchema,
  confirmRoyaltyDepositRequestSchema,
  createAdminRequestSchema,
  createPolicyRequestSchema,
  createRefundRequestSchema,
  dismissDisputeRequestSchema,
  dismissRoyaltyObjectionRequestSchema,
  fillCompanyPlaceholders,
  formatFollowerBoundary,
  formatFollowerCount,
  holdSettlementRequestSchema,
  listAdminCommunityPostsQuerySchema,
  listReportsQuerySchema,
  listSocialChannelRequestsQuerySchema,
  listSupportInquiriesQuerySchema,
  lowestMinFollowers,
  myMfaSetupResponseSchema,
  operatorSignInResponseSchema,
  overpaymentReturnSchema,
  payOutRequestSchema,
  platformAssetKeySchema,
  platformAssetMetaSchema,
  platformAssetPath,
  platformBankAccountSchema,
  platformCatalogSchema,
  platformCommunitySettingsSchema,
  platformCompanySettingsSchema,
  platformListingSettingsSchema,
  platformPaymentSettingsSchema,
  platformSettingsSchema,
  platformSiteSettingsSchema,
  platformSiteTextSchema,
  platformSocialLinksSchema,
  platformSponsorshipSettingsSchema,
  policyVersionContentSchema,
  policyVersionDetailSchema,
  policyVersionSummarySchema,
  proposeLicenseAmendmentRequestSchema,
  publishPolicyVersionRequestSchema,
  refundBankAccountSchema,
  rejectListingRequestSchema,
  rejectRefundRequestSchema,
  rejectSocialChannelRequestSchema,
  rejectSponsorshipRequestSchema,
  removeCommunityContentRequestSchema,
  reportPendingCountSchema,
  resetAdminPasswordRequestSchema,
  resolveChatReportRequestSchema,
  resolveCommunityReportRequestSchema,
  resolveCommunityTier,
  resolveViolationReportRequestSchema,
  resumeLicenseRequestSchema,
  returnDepositRequestSchema,
  reviveOrderRequestSchema,
  revokeSocialChannelRequestSchema,
  schedulerJobKeySchema,
  schedulerJobResponseSchema,
  seenVersionSchema,
  siteConfigResponseSchema,
  sponsorshipCategorySchema,
  suspendLicenseRequestSchema,
  suspendListingRequestSchema,
  suspendUserRequestSchema,
  takeDownSponsorshipRequestSchema,
  terminateOrderRequestSchema,
  unsuspendUserRequestSchema,
  updateAdminMfaSettingRequestSchema,
  updateAppVersionRequestSchema,
  updateCommunitySettingsRequestSchema,
  updateCompanySettingsRequestSchema,
  updateDepositReturnAccountRequestSchema,
  updateListingSettingsRequestSchema,
  updateMyProfileRequestSchema,
  updatePaymentSettingsRequestSchema,
  updatePlatformSocialLinksRequestSchema,
  updatePolicyRequestSchema,
  updateSchedulerJobRequestSchema,
  updateSiteSettingsRequestSchema,
  updateSponsorshipSettingsRequestSchema,
  upsertPolicyContentRequestSchema,
  voidRoyaltyReportRequestSchema
};
//# sourceMappingURL=index.js.map